import { pageViewTypes } from "@constants/tracking";
/* ----------------- React --------------- */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useExamsFilterDialog } from "@contexts/DialogContext/dialogs/exams-filter";
import { usePurchaseDialog } from "@contexts/DialogContext/dialogs/purchase";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
/* ----------------- Hooks --------------- */
import { useMoreSettings } from "../hooks/useMoreSettings";

import { Portal } from "react-native-paper";

import { AppBar } from "@features/dashboard/layouts/AppBar";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { isWeb } from "@memorang/helpers/platform";
import SnackBarCustom from "@memorang/ui/components/CustomSnackbar";
import MoreApplet from "@memorang/ui/components/MoreApplet";
import type { FontSchemaType } from "@memorang/ui/font";
import SetExamDateContainer from "components/SetExamDateContainer";
/* ----------------- Helpers --------------- */
import { router } from "expo-router";
/* ----------------- Types --------------- */
import type { MoreSettingsItem } from "../types";

const MoreRouteContainer = () => {
	const { t } = useTranslation();
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const {
		moreSettingsSections,
		showDatePicker,
		toggleShowDatePicker,
		entitlements,
		canSwitchExam,
		canExtendSubscription,
	} = useMoreSettings();
	const memoizedAnalyticsParams = useMemo(() => {
		return {
			type: pageViewTypes.more,
		};
	}, []);
	useAnalytics(memoizedAnalyticsParams);
	const setShowAvatarUpdatedMessage = useGlobalStore(
		(state) => state.setShowAvatarUpdatedMessage,
	);

	const showAvatarUpdatedMessage = useGlobalStore(
		(state) => state.showAvatarUpdatedMessage,
	);
	const toggleShowFeedbackDialog = () => {
		setShowFeedbackDialog((prev) => !prev);
	};
	const { showPurchase } = usePurchaseDialog();

	const { showExamsFilter } = useExamsFilterDialog();

	const handleNavigateToPurchaseView = (id?: string) => {
		const entitlement = entitlements?.find((item) => item.id === id);
		if (entitlement) {
			showPurchase({ productType: entitlement.productType });
		}
	};
	const appFont = useAppStore((store) => store.app.font);

	const handleClickMoreSettingsItem = (item: MoreSettingsItem) => {
		switch (item.type) {
			case "profile":
				router.push("/profile");
				break;
			case "account-settings":
				router.push({
					pathname: "/account/settings",
					params: { showBackButton: "true" },
				});
				break;
			case "change-date":
				toggleShowDatePicker();
				break;
			case "switch-exam":
				if (canSwitchExam) {
					showExamsFilter();
				}
				break;
			case "subscription":
				if (canExtendSubscription) {
					handleNavigateToPurchaseView(item.id);
				}
				break;
			case "redeem":
				router.push("/redeem");
				break;
			default:
				break;
		}
	};

	return (
		<>
			<AppBar title={t("appbar.more.title")} hideExamSwitcher />
			<MoreApplet
				sections={moreSettingsSections}
				handleClickMoreSettingsItem={handleClickMoreSettingsItem}
				appFont={appFont as FontSchemaType}
			/>
			<SetExamDateContainer
				showDatePicker={showDatePicker}
				toggleShowDatePicker={toggleShowDatePicker}
			/>
			{showAvatarUpdatedMessage && (
				<Portal>
					<SnackBarCustom
						message="Avatar updated successfully"
						duration={2000}
						visible
						usePortal={false}
						handleDismiss={() => {
							setShowAvatarUpdatedMessage(false);
						}}
					/>
				</Portal>
			)}
			{isWeb && (
				<FeedbackDialogContainer
					visible={showFeedbackDialog}
					onDismiss={toggleShowFeedbackDialog}
				/>
			)}
		</>
	);
};

export default MoreRouteContainer;
